import React, { useState, useEffect } from 'react';
import { FaUpload, FaTrash, FaSpinner } from 'react-icons/fa';
import api from '../../services/api';

const GalleryManagement = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    
    // Form state
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('Event');
    
    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        try {
            const res = await api.get('/gallery');
            setImages(res.data.data);
        } catch (error) {
            console.error('Error fetching gallery:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file || !title) return alert('File and Title are required');
        
        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);
        formData.append('title', title);
        formData.append('description', description);
        formData.append('category', category);

        try {
            await api.post('/gallery', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            alert('Image uploaded successfully');
            // reset form
            setFile(null);
            setTitle('');
            setDescription('');
            setCategory('Event');
            // refresh list
            fetchImages();
        } catch (error) {
            console.error('Error uploading image:', error);
            alert(error.response?.data?.message || 'Error uploading image');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this image?')) return;
        try {
            await api.delete(`/gallery/${id}`);
            alert('Image deleted successfully');
            fetchImages();
        } catch (error) {
            console.error('Error deleting image:', error);
            alert('Error deleting image');
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-4 mb-6">Gallery Management</h2>
            
            {/* Upload Form */}
            <form onSubmit={handleUpload} className="mb-10 p-6 bg-gray-50 rounded-xl border border-gray-200">
                <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                    <FaUpload className="mr-2 text-orange-500" /> Add New Photo
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Photo Title *</label>
                        <input
                            type="text"
                            required
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                            placeholder="E.g., Annual Concert 2026"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <select
                            value={category}
                            onChange={e => setCategory(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                        >
                            <option value="Event">Event</option>
                            <option value="Performance">Performance</option>
                            <option value="Classes">Classes</option>
                            <option value="Recognition">Recognition</option>
                            <option value="Stage">Stage</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                        <textarea
                            rows="2"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                            placeholder="Add some details about the photo..."
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image File *</label>
                        <input
                            type="file"
                            required
                            accept="image/*"
                            onChange={e => setFile(e.target.files[0])}
                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 border border-gray-300 rounded-lg p-2"
                        />
                    </div>
                </div>
                
                <div className="mt-6 flex justify-end">
                    <button
                        type="submit"
                        disabled={uploading}
                        className="bg-orange-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-orange-700 transition flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {uploading ? <FaSpinner className="animate-spin mr-2" /> : <FaUpload className="mr-2" />}
                        {uploading ? 'Uploading...' : 'Upload Photo'}
                    </button>
                </div>
            </form>

            {/* Existing Images */}
            <h3 className="text-lg font-medium text-gray-800 mb-4 border-b pb-2">Uploaded Photos</h3>
            {loading ? (
                <div className="flex justify-center py-10"><FaSpinner className="animate-spin text-3xl text-orange-500" /></div>
            ) : images.length === 0 ? (
                <p className="text-gray-500 text-center py-10">No photos uploaded yet.</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {images.map(img => (
                        <div key={img._id} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm group relative">
                            <div className="aspect-w-4 aspect-h-3 bg-gray-100">
                                <img src={img.imageUrl} alt={img.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-4">
                                <div className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-1">{img.category}</div>
                                <h4 className="font-semibold px-0 text-gray-800 truncate mb-1">{img.title}</h4>
                                <p className="text-xs text-gray-500 line-clamp-2">{img.description || 'No description'}</p>
                            </div>
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleDelete(img._id)}
                                    className="bg-red-600 text-white p-2 rounded-full shadow-lg hover:bg-red-700 hover:scale-110 transition flex items-center justify-center"
                                    title="Delete Photo"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default GalleryManagement;
